import { useState, useEffect, useRef, useCallback } from 'react';

interface UseTimerParams {
  expiryTimestamp: Date | number;
  autoStart?: boolean;
  onExpire?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onReset?: () => void;
  interval?: number; // default 1000 ms
}

interface UseTimerResult {
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  isRunning: boolean;
  start: () => void;
  pause: () => void;
  resume: () => void;
  reset: (newExpiryTimestamp?: Date | number) => void;
  restart: (newExpiryTimestamp?: Date | number) => void;
}

export const useTimer = ({
  expiryTimestamp,
  autoStart,
  onExpire,
  onPause,
  onResume,
  onReset,
  interval = 1000,
}: UseTimerParams): UseTimerResult => {
  const expiryTime = useRef<number>(typeof expiryTimestamp === 'number' ? expiryTimestamp : expiryTimestamp.getTime());
  const [isRunning, setIsRunning] = useState(autoStart);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every interval when running
  useEffect(() => {
    const tick = () => {
      setCurrentTime(Date.now());
    };
    const id = isRunning && setInterval(tick, interval);
    return () => id && clearInterval(id);
  }, [isRunning, interval]);

  // Calculate time left and trigger onExpire when zero
  useEffect(() => {
    const diff = expiryTime.current - currentTime;
    if (diff <= 0 && isRunning) {
      setIsRunning(false);
      if (onExpire) onExpire();
    }
  }, [currentTime, isRunning]);

  // Calculate time parts from diff
  const diff = Math.max(expiryTime.current - currentTime, 0);
  const totalSeconds = Math.floor(diff / 1000);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const totalHours = Math.floor(totalMinutes / 60);
  const hours = totalHours % 24;
  const days = Math.floor(totalHours / 24);

  const start = useCallback(() => {
    if (!isRunning) {
      setIsRunning(true);
    }
  }, [isRunning]);

  const pause = useCallback(() => {
    if (isRunning) {
      setIsRunning(false);
      if (onPause) onPause();
    }
  }, [isRunning, onPause]);

  const resume = useCallback(() => {
    if (!isRunning && diff > 0) {
      setIsRunning(true);
      if (onResume) onResume();
    }
  }, [isRunning, diff, onResume]);

  const reset = useCallback(
    (newExpiryTimestamp?: Date | number, shouldAutoStart?: boolean) => {
      if (newExpiryTimestamp) {
        if (typeof newExpiryTimestamp === 'number') {
          expiryTime.current = newExpiryTimestamp;
        } else if (typeof newExpiryTimestamp === 'string') {
          expiryTime.current = new Date(newExpiryTimestamp).getTime();
        } else {
          expiryTime.current = newExpiryTimestamp.getTime?.();
        }
      }
      setCurrentTime(Date.now());
      if (onReset) onReset();
      setIsRunning(!!shouldAutoStart);
    },
    [autoStart, onReset],
  );

  const restart = useCallback(
    (newExpiryTimestamp?: Date | number) => {
      reset(newExpiryTimestamp, true);
      setIsRunning(true);
    },
    [reset],
  );

  return {
    seconds,
    minutes,
    hours,
    days,
    isRunning,
    start,
    pause,
    resume,
    reset,
    restart,
  };
};
